import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { GroupedTable } from "@/components/GroupedTable/GroupedTable";
import { InviteOrganizationMemberDialog } from "@/components/organization/InviteOrganizationMemberDialog";
import { OrganizationRoleSelection } from "@/components/user/RoleSelection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Column, Row } from "@/components/ui/flex";
import { TableSkeleton } from "@/components/ui/skeleton";
import {
  OrganizationMember,
  useOrganizationData,
  useOrganizationMembersData,
} from "@/data/use-organizations-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faUnlink,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { AvatarFallback } from "@radix-ui/react-avatar";
import { Link, useParams } from "@tanstack/react-router";
import { toast } from "sonner";

export default function GlobalAdminOrganizationMembersPage() {
  const { organizationUuid } = useParams({ strict: false }) as {
    organizationUuid?: string;
  };
  const { organization } = useOrganizationData(organizationUuid);
  const { members, isLoading, removeMember, updateMemberRole, isUpdatingRole } =
    useOrganizationMembersData(organizationUuid ?? null);

  const handleRemove = async (member: OrganizationMember) => {
    try {
      await removeMember(member.uuid);
      toast.success(`Removed ${member.email}.`);
    } catch {
      toast.error("Failed to remove member.");
    }
  };

  if (!organization) return null;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Members</PageHeader.Title>
      </PageHeader>
      <PageBody innerClassName="gap-8">
        <Row className="items-center justify-between">
          <h2 className="text-xl font-bold">Members</h2>
          <InviteOrganizationMemberDialog organizationUuid={organizationUuid} />
        </Row>
        {isLoading && <TableSkeleton />}
        {!isLoading && members.length === 0 && (
          <div className="text-muted-foreground py-4 text-center">
            No members yet.
          </div>
        )}
        {!isLoading && members.length > 0 && (
          <GroupedTable
            data={[
              {
                groupId: "members",
                label: "Members",
                items: members,
              },
            ]}
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "role", label: "Organization Role" },
            ]}
            renderCell={{
              name: (_, row) => <UserDisplayName user={row} />,
              role: (value, row) => (
                <OrganizationRoleSelection
                  value={value as OrganizationMember["role"]}
                  disabled={isUpdatingRole}
                  onChange={(role) => {
                    void updateMemberRole(
                      { userUuid: row.uuid, role },
                      {
                        onSuccess: () => {
                          toast.success("Organization role updated.");
                        },
                        onError: () => {
                          toast.error("Failed to update organization role.");
                        },
                      },
                    );
                  }}
                />
              ),
            }}
            renderActions={({ row }) => (
              <Row className="items-center gap-2">
                <Link
                  to="/admin/users/$userUuid"
                  params={{ userUuid: row.uuid }}
                >
                  <FontAwesomeIcon icon={faEye} />
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-destructive hover:text-destructive-foreground"
                      title="Remove"
                    >
                      <FontAwesomeIcon icon={faUnlink} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Remove from organization?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        <Column className="gap-2">
                          <div className="relative flex gap-2 self-center p-4">
                            <UserDisplayName user={row} variant="large" />
                            <Avatar className="bg-destructive absolute -top-1 -right-1 h-12 w-12 text-white">
                              <AvatarFallback>
                                <FontAwesomeIcon icon={faUnlink} />
                              </AvatarFallback>
                            </Avatar>
                          </div>
                          <div>
                            Remove {row.name} ({row.email}) from this
                            organization?
                          </div>
                        </Column>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => void handleRemove(row)}
                        asChild
                      >
                        <Button size="sm">Remove</Button>
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </Row>
            )}
          />
        )}
      </PageBody>
    </Page>
  );
}
